import type { KodKarsilastirma, RecordComparison, TenantAccess } from '@konyaxl/shared';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AuthImage } from '../components/AuthImage.js';
import { CameraScanner, cameraSupported } from '../components/CameraScanner.js';
import { QrDialog } from '../components/QrDialog.js';
import { ApiRequestError, api, downloadFile } from '../core/api.js';
import { onHardwareScan } from '../core/native.js';
import { useAuth } from '../core/auth.js';
import { bugunYerel } from '../core/tarih.js';
import { startScannerListener } from '../core/scanner.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
  /** Üstte gösterilecek sekme şeridi; tek başına açıldığında verilmez. */
  sekmeler?: ReactNode;
}

interface Kayit {
  id: string;
  codeA: string | null;
  codeB: string | null;
  createdAt: string;
  createdByName: string;
  /** İade akışında yazıldığı alt kategori. */
  kategori?: string | null;
  /** Asıl kategoriyle karşılaştırma — yalnızca iade kategorisinde dolu. */
  compare?: RecordComparison | null;
  /** Barkodsuz kayıtta ürün kodunun asıl kategorideki durumu. */
  kodDurum?: KodKarsilastirma | null;
  aciklama?: string | null;
  imageId?: string | null;
}

/** Karşılaştırma rozetinin görünümü. */
const ROZET: Record<
  RecordComparison['durum'],
  { isaret: string; sinif: string; baslik: string }
> = {
  eslesiyor: { isaret: '✓', sinif: 'yesil', baslik: 'Eşleştirme doğru' },
  farkli: {
    isaret: '!',
    sinif: 'kirmizi',
    baslik: 'Eşleştirme tutmuyor — doğrusunu görmek için tıklayın',
  },
  'kayit-yok': {
    isaret: '–',
    sinif: 'gri',
    baslik: 'Asıl kategoride bu barkod kayıtlı değil',
  },
};

interface Sayfa {
  items: Kayit[];
  nextCursor: string | null;
}

const bugun = bugunYerel;

export function RecordsPage({ tenant, onBack, sekmeler }: Props) {
  const { tenants } = useAuth();
  const [arama, setArama] = useState('');
  const [tarih, setTarih] = useState(bugun());
  const [tarihFiltresi, setTarihFiltresi] = useState(true);
  const [kayitlar, setKayitlar] = useState<Kayit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [seciliKayit, setSeciliKayit] = useState<Kayit | null>(null);
  const [kameraAcik, setKameraAcik] = useState(false);
  const [uyusmazlik, setUyusmazlik] = useState<Kayit | null>(null);
  /** Boş dize = tüm kategoriler. */
  const [kategori, setKategori] = useState('');
  const [buyukGorsel, setBuyukGorsel] = useState<string | null>(null);
  const [eslesme, setEslesme] = useState<Kayit | null>(null);

  /** Eşleştirmesiz kategoride ürün kodu, fotoğraf ve açıklama gösterilir. */
  const tekKayit = tenant.features.singleEntry;

  /** Karşılaştırma sütunu yalnızca denetlenen kategorilerde görünür. */
  const karsilastirmaVar = Boolean(tenant.compareWith);
  /** Kullanıcıya slug değil kategori adı gösterilir. */
  const karsiAd =
    tenants.find((t) => t.slug === tenant.compareWith)?.name ?? tenant.compareWith;

  const istekSayaci = useRef(0);

  const yukle = useCallback(
    async (opts: {
      q: string;
      date: string | null;
      kategori: string;
      devam?: string | null;
    }) => {
      const benim = ++istekSayaci.current;
      setYukleniyor(true);
      setHata(null);

      const params = new URLSearchParams();
      if (opts.q.trim()) params.set('q', opts.q.trim());
      if (opts.date) params.set('date', opts.date);
      if (opts.kategori) params.set('kategori', opts.kategori);
      if (opts.devam) params.set('cursor', opts.devam);
      params.set('limit', '50');

      try {
        const res = await api.get<Sayfa>(`/${tenant.slug}/records?${params}`);
        // Arama sırasında hızlı yazımda eski yanıt sonra gelebilir; yoksay.
        if (benim !== istekSayaci.current) return;

        setKayitlar((eski) => (opts.devam ? [...eski, ...res.items] : res.items));
        setCursor(res.nextCursor);
      } catch (err) {
        if (benim !== istekSayaci.current) return;
        setHata(
          err instanceof ApiRequestError ? err.message : 'Kayıtlar yüklenemedi',
        );
      } finally {
        if (benim === istekSayaci.current) setYukleniyor(false);
      }
    },
    [tenant.slug],
  );

  /**
   * Okunan barkod doğrudan aramaya yazılır.
   *
   * Kamera okuma sonrası kapanır: burada yapılan iş tek bir kodu sorgulamak,
   * tarama ekranındaki gibi arka arkaya okuma değil. Açık bırakmak hem pil
   * harcar hem sonuç listesini okumayı zorlaştırır.
   */
  const koduAra = useCallback((kod: string) => {
    const temiz = kod.trim();
    if (!temiz) return;
    setArama(temiz);
    setKameraAcik(false);
    /**
     * Tarih filtresi kaldırılır: bir barkod okutuluyorsa soru "bu kod sistemde
     * var mı", hangi gün girildiği değil. Filtre açık kalsa bugüne ait olmayan
     * kod "bulunamadı" görünürdü. Eski projede de arama tarihten bağımsızdı
     * (rabihome.dart:103).
     */
    setTarihFiltresi(false);
  }, []);

  /**
   * El terminalinin okuyucusu ve USB okuyucular. Arama kutusu odaktayken
   * kod zaten kutuya yazıldığı için dinleyici orada devre dışı kalıyor.
   */
  useEffect(() => {
    const wedgeDurdur = startScannerListener({ onScan: koduAra });
    const dwDurdur = onHardwareScan(koduAra);
    return () => {
      wedgeDurdur();
      dwDurdur();
    };
  }, [koduAra]);

  /**
   * Arama yazarken her tuşta istek atmamak için kısa bekleme. Barkod
   * okuyucuyla girildiğinde kod tek seferde geldiği için gecikme hissedilmez.
   */
  useEffect(() => {
    const zaman = setTimeout(() => {
      void yukle({ q: arama, date: tarihFiltresi ? tarih : null, kategori });
    }, 250);
    return () => clearTimeout(zaman);
  }, [arama, tarih, tarihFiltresi, kategori, yukle]);

  async function excelIndir() {
    setIndiriliyor(true);
    setHata(null);
    try {
      const params = new URLSearchParams();
      if (tarihFiltresi) {
        params.set('from', tarih);
        params.set('to', tarih);
      }
      await downloadFile(
        `/${tenant.slug}/export.xlsx?${params}`,
        `${tenant.slug}.xlsx`,
      );
    } catch {
      setHata('Excel dosyası indirilemedi');
    } finally {
      setIndiriliyor(false);
    }
  }

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Kayıtlar</h1>
        <div className="ust-bar-sag">
          <button className="btn-excel" onClick={excelIndir} disabled={indiriliyor}>
            {indiriliyor ? 'Hazırlanıyor…' : 'Excel indir'}
          </button>
        </div>
      </header>

      {sekmeler}

      <main className="icerik">
        <div className="filtre-cubugu">
          <input
            className="arama-kutu"
            /**
             * Okuyucu dinleyicisi bu alanda devre dışı: odak buradayken
             * kod zaten doğrudan kutuya yazılıyor. İşaretlenmezse aynı
             * okuma hem kutuya girer hem dinleyiciyi tetikler.
             */
            data-manual-input="true"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            // Eşleştirmesiz kategoride ikinci kod yok; iki alan adı yazmak yanıltır.
            placeholder={
              tekKayit
                ? `${tenant.scanRules.a.label} ara`
                : `${tenant.scanRules.a.label} veya ${tenant.scanRules.b.label} ara`
            }
            autoFocus
          />

          {cameraSupported() && (
            <button
              className={`btn-kamera ${kameraAcik ? 'acik' : ''}`}
              onClick={() => setKameraAcik((a) => !a)}
              title="Kamera ile barkod okut"
            >
              {kameraAcik ? 'Kamerayı kapat' : 'Kamera'}
            </button>
          )}

          {tenant.subCategories.length > 0 && (
            <select
              className="kategori-suzgec"
              value={kategori}
              onChange={(e) => setKategori(e.target.value)}
              title="Kategoriye göre süz"
            >
              <option value="">Tüm kategoriler</option>
              {tenant.subCategories.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          )}

          <label className="filtre-onay">
            <input
              type="checkbox"
              checked={tarihFiltresi}
              onChange={(e) => setTarihFiltresi(e.target.checked)}
            />
            Tarihe göre
          </label>

          <input
            type="date"
            className="tarih-kutu"
            value={tarih}
            disabled={!tarihFiltresi}
            max={bugun()}
            onChange={(e) => setTarih(e.target.value)}
          />
        </div>

        {kameraAcik && (
          <div className="kamera-arama">
            <CameraScanner onScan={koduAra} onClose={() => setKameraAcik(false)} />
          </div>
        )}

        {hata && <div className="hata-kutu">{hata}</div>}

        <div className="sonuc-bilgi">
          {yukleniyor && kayitlar.length === 0
            ? 'Yükleniyor…'
            : `${kayitlar.length} kayıt${cursor ? '+' : ''}`}
          {arama.trim() && !tarihFiltresi && ' · tüm tarihlerde arandı'}
        </div>

        {!yukleniyor && kayitlar.length === 0 ? (
          <div className="bos-durum">
            {arama.trim()
              ? `"${arama.trim()}" için kayıt bulunamadı`
              : 'Bu tarihte kayıt yok'}
          </div>
        ) : (
          <table className="kayit-tablo">
            <thead>
              <tr>
                {karsilastirmaVar && <th aria-label="Durum" />}
                {tekKayit && <th>Fotoğraf</th>}
                <th>{tenant.scanRules.a.label}</th>
                {/* Eşleştirme yapmayan kategoride ikinci kod diye bir şey yok. */}
                {!tekKayit && <th>{tenant.scanRules.b.label}</th>}
                {tekKayit && <th>Açıklama</th>}
                {tenant.subCategories.length > 0 && <th>Kategori</th>}
                <th>Tarih</th>
                <th>Saat</th>
                <th>Okutan</th>
              </tr>
            </thead>
            <tbody>
              {kayitlar.map((k) => {
                const d = new Date(k.createdAt);
                return (
                  <tr
                    key={k.id}
                    className="tiklanabilir"
                    onClick={() => setSeciliKayit(k)}
                    title="Kodları kopyalamak ve QR görmek için tıklayın"
                  >
                    {karsilastirmaVar && (
                      <td className="durum-hucre">
                        {tekKayit && k.kodDurum && (
                          <button
                            className={`karsilastirma-rozet ${
                              k.kodDurum.durum === 'bulundu' ? 'kirmizi' : 'gri'
                            }`}
                            title={
                              k.kodDurum.durum === 'bulundu'
                                ? `Bu ürün kodu ${karsiAd} kategorisinde kayıtlı — görmek için tıklayın`
                                : `${karsiAd} kategorisinde bu ürün kodu yok`
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (k.kodDurum?.durum === 'bulundu') setEslesme(k);
                            }}
                          >
                            {k.kodDurum.durum === 'bulundu' ? '!' : '–'}
                          </button>
                        )}
                        {!tekKayit && k.compare && (
                          <button
                            className={`karsilastirma-rozet ${ROZET[k.compare.durum].sinif}`}
                            title={ROZET[k.compare.durum].baslik}
                            // Satırın QR penceresi açılmasın; rozetin kendi işi var.
                            onClick={(e) => {
                              e.stopPropagation();
                              if (k.compare?.durum === 'farkli') setUyusmazlik(k);
                            }}
                          >
                            {ROZET[k.compare.durum].isaret}
                          </button>
                        )}
                      </td>
                    )}
                    {tekKayit && (
                      <td className="foto-hucre">
                        {k.imageId && (
                          <AuthImage
                            path={`/${tenant.slug}/images/${k.imageId}`}
                            alt="Ürün fotoğrafı"
                            className="kucuk-foto"
                            onClick={() => setBuyukGorsel(k.imageId!)}
                          />
                        )}
                      </td>
                    )}
                    <td className="kod">{k.codeA || <em className="kodsuz">kodsuz</em>}</td>
                    {!tekKayit && <td className="kod">{k.codeB}</td>}
                    {tekKayit && <td className="aciklama-hucre">{k.aciklama}</td>}
                    {tenant.subCategories.length > 0 && (
                      <td>
                        {k.kategori && <span className="kategori-rozet">{k.kategori}</span>}
                      </td>
                    )}
                    <td>{d.toLocaleDateString('tr-TR')}</td>
                    <td>{d.toLocaleTimeString('tr-TR', { hour12: false })}</td>
                    <td>{k.createdByName}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {cursor && (
          <button
            className="btn-daha"
            disabled={yukleniyor}
            onClick={() =>
              void yukle({
                q: arama,
                date: tarihFiltresi ? tarih : null,
                kategori,
                devam: cursor,
              })
            }
          >
            {yukleniyor ? 'Yükleniyor…' : 'Daha fazla göster'}
          </button>
        )}
      </main>

      {uyusmazlik?.compare && (
        <div className="onay-katman" role="dialog" aria-modal="true">
          <div className="onay-kutu">
            <h2>Eşleştirme tutmuyor</h2>
            <p className="onay-kod">{uyusmazlik.codeA}</p>

            <div className="karsilastirma-satir">
              <span className="karsilastirma-etiket">Bu kayıtta</span>
              <span className="kod kirmizi-metin">{uyusmazlik.codeB || '—'}</span>
            </div>
            <div className="karsilastirma-satir">
              <span className="karsilastirma-etiket">Doğrusu</span>
              <span className="kod yesil-metin">
                {uyusmazlik.compare.dogruKodB || '—'}
              </span>
            </div>

            <p className="onay-aciklama">
              Doğru eşleştirme <strong>{karsiAd}</strong> kategorisindeki kayda göre
              gösteriliyor.
            </p>

            <div className="onay-butonlar">
              <button className="btn-devam" onClick={() => setUyusmazlik(null)} autoFocus>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {eslesme?.kodDurum && (
        <div className="onay-katman" role="dialog" aria-modal="true">
          <div className="onay-kutu">
            <h2>Bu ürün kodu kayıtlı</h2>
            <p className="onay-kod">{eslesme.codeA}</p>
            <div className="karsilastirma-satir">
              <span className="karsilastirma-etiket">Eşleştiği barkod</span>
              <span className="kod yesil-metin">
                {eslesme.kodDurum.eslesenBarkod || '—'}
              </span>
            </div>
            <p className="onay-aciklama">
              Bu ürün kodu <strong>{karsiAd}</strong> kategorisinde yukarıdaki
              barkodla eşleştirilmiş.
            </p>
            <div className="onay-butonlar">
              <button className="btn-devam" onClick={() => setEslesme(null)} autoFocus>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {buyukGorsel && (
        <div
          className="gorsel-katman"
          role="dialog"
          aria-modal="true"
          onClick={() => setBuyukGorsel(null)}
        >
          <AuthImage
            path={`/${tenant.slug}/images/${buyukGorsel}`}
            alt="Ürün fotoğrafı"
            className="buyuk-foto"
          />
        </div>
      )}

      {seciliKayit && (
        <QrDialog
          kodlar={[
            { etiket: tenant.scanRules.a.label, deger: seciliKayit.codeA ?? '' },
            { etiket: tenant.scanRules.b.label, deger: seciliKayit.codeB ?? '' },
          ].filter((k) => k.deger)}
          altBilgi={seciliKayit.aciklama || undefined}
          altNot={tenant.name}
          onClose={() => setSeciliKayit(null)}
          onDelete={
            tenant.canWrite
              ? async () => {
                  await api.del(`/${tenant.slug}/records/${seciliKayit.id}`);
                  // Listeden hemen çıkar; yeniden yükleme beklenmesin.
                  setKayitlar((eski) => eski.filter((k) => k.id !== seciliKayit.id));
                }
              : undefined
          }
        />
      )}
    </>
  );
}
